import { apiRequest } from "./api";

export const SPACE_ROLE_OPTIONS = Object.freeze([
  Object.freeze({ value: "space_admin", label: "空间管理员" }),
  Object.freeze({ value: "editor", label: "创作成员" }),
  Object.freeze({ value: "viewer", label: "只读成员" }),
]);

function requiredID(value, label) {
  const normalized = String(value || "").trim();
  if (!normalized) {
    throw new Error(`${label}不能为空`);
  }
  return encodeURIComponent(normalized);
}

function normalizeContext(payload) {
  return {
    space: payload && payload.space ? payload.space : null,
    roles: payload && Array.isArray(payload.roles) ? payload.roles : [],
    permissions: payload && Array.isArray(payload.permissions) ? payload.permissions : [],
  };
}

function commandIdempotencyKey(prefix) {
  const cryptoAPI = typeof window !== "undefined" ? window.crypto : null;
  const randomPart = cryptoAPI && typeof cryptoAPI.randomUUID === "function"
    ? cryptoAPI.randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
  return `${prefix}-${randomPart}`;
}

export async function listMySpaces() {
  const payload = await apiRequest("/api/v1/me/spaces");
  return payload && Array.isArray(payload.spaces) ? payload.spaces : [];
}

export async function getCurrentSpaceContext() {
  return normalizeContext(await apiRequest("/api/v1/spaces/current"));
}

export async function createSpace(input = {}) {
  const name = String(input.name || "").trim();
  const slug = String(input.slug || "").trim();
  if (!name) {
    throw new Error("空间名称不能为空");
  }
  return normalizeContext(await apiRequest("/api/v1/spaces", {
    method: "POST",
    body: JSON.stringify({ name, slug }),
  }));
}

export async function switchCurrentSpace(spaceID) {
  const id = requiredID(spaceID, "空间 ID");
  return normalizeContext(await apiRequest(`/api/v1/me/spaces/${id}/switch`, {
    method: "POST",
  }));
}

export async function setDefaultSpace(spaceID) {
  const id = requiredID(spaceID, "空间 ID");
  return normalizeContext(await apiRequest(`/api/v1/me/spaces/${id}/default`, {
    method: "POST",
  }));
}

export async function leaveSpace(spaceID) {
  const id = requiredID(spaceID, "空间 ID");
  return apiRequest(`/api/v1/me/spaces/${id}/leave`, {
    method: "DELETE",
  });
}

export async function transferSpaceOwnership(spaceID, membershipID, options = {}) {
  const space = requiredID(spaceID, "空间 ID");
  const member = requiredID(membershipID, "成员 ID");
  const idempotencyKey = String(options.idempotencyKey || commandIdempotencyKey("space-owner-transfer")).trim();
  return apiRequest(`/api/v1/platform/tenants/${space}/owner`, {
    method: "POST",
    headers: { "Idempotency-Key": idempotencyKey },
    body: JSON.stringify({ membership_id: decodeURIComponent(member) }),
  });
}

export async function listSpaceMembers(spaceID) {
  const id = requiredID(spaceID, "空间 ID");
  const payload = await apiRequest(`/api/v1/spaces/${id}/members`);
  return {
    space: payload && payload.space ? payload.space : null,
    members: payload && Array.isArray(payload.members) ? payload.members : [],
  };
}

export async function inviteSpaceMember(spaceID, input = {}) {
  const id = requiredID(spaceID, "空间 ID");
  const identifier = String(input.identifier || "").trim();
  const roleCodes = Array.isArray(input.roleCodes) ? input.roleCodes.filter(Boolean) : [];
  if (!identifier) {
    throw new Error("成员账号不能为空");
  }
  const payload = await apiRequest(`/api/v1/spaces/${id}/members/invite`, {
    method: "POST",
    body: JSON.stringify({ identifier, role_codes: roleCodes }),
  });
  return payload && payload.member ? payload.member : null;
}

export async function updateSpaceMemberRoles(spaceID, membershipID, roleCodes) {
  const space = requiredID(spaceID, "空间 ID");
  const member = requiredID(membershipID, "成员 ID");
  const roles = Array.isArray(roleCodes) ? roleCodes.filter(Boolean) : [];
  if (!roles.length) {
    throw new Error("至少选择一个成员角色");
  }
  const payload = await apiRequest(`/api/v1/spaces/${space}/members/${member}/roles`, {
    method: "PATCH",
    body: JSON.stringify({ role_codes: roles }),
  });
  return payload && payload.member ? payload.member : null;
}

export async function updateSpaceMemberStatus(spaceID, membershipID, status) {
  const space = requiredID(spaceID, "空间 ID");
  const member = requiredID(membershipID, "成员 ID");
  const normalizedStatus = String(status || "").trim();
  if (!["active", "suspended"].includes(normalizedStatus)) {
    throw new Error("成员状态无效");
  }
  const payload = await apiRequest(`/api/v1/spaces/${space}/members/${member}/status`, {
    method: "PATCH",
    body: JSON.stringify({ status: normalizedStatus }),
  });
  return payload && payload.member ? payload.member : null;
}

export async function removeSpaceMember(spaceID, membershipID) {
  const space = requiredID(spaceID, "空间 ID");
  const member = requiredID(membershipID, "成员 ID");
  return apiRequest(`/api/v1/spaces/${space}/members/${member}`, {
    method: "DELETE",
  });
}
